import { useState, useEffect, useCallback, useMemo } from "react";
import { Cloud, Loader2, Search, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ProfileAvatar } from "@/components/common/ProfileAvatar";
import { getProfileDisplayName } from "@/lib/profileUtils";
import { cn } from "@/lib/utils";
import {
  useAzureUserSearch,
  type AzureUser,
  type AssignmentContext,
} from "@/hooks/useAzureUserSearch";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

export interface ProfileSelectProfile {
  user_id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  display_name: string | null;
  avatar_url: string | null;
  is_provisioned?: boolean;
}

interface ProfileSelectProps {
  profiles: ProfileSelectProfile[];
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  allowUnassigned?: boolean;
  unassignedLabel?: string;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  id?: string;
  /**
   * Test hook forwarded to the underlying SelectTrigger (mirrors the
   * `data-testid` convention already used by plain `<Select>` fields, e.g.
   * CreateInspectionDialog's checklist-select). Needed because the trigger's
   * own `aria-label` (see below) shadows an external wrapping `<Label>` for
   * accessible-name computation, so `getByLabel(...)` alone cannot reliably
   * target this control from an E2E spec (TES-2273).
   */
  dataTestId?: string;
  /** Enable real-time Azure AD search for SSO tenants */
  enableAzureSearch?: boolean;
  /** Role to assign when provisioning Azure users (default: mangelbearbeiter) */
  provisionRole?: string;
  /** Assignment context to filter Azure search results */
  assignmentContext?: AssignmentContext;
}

/**
 * A reusable people picker component with avatar support.
 * Shows profile avatars alongside names in both trigger and dropdown.
 * Optionally supports real-time Azure AD search for SSO-enabled tenants.
 */
export function ProfileSelect({
  profiles,
  value,
  onValueChange,
  placeholder,
  allowUnassigned = false,
  unassignedLabel,
  loading = false,
  disabled = false,
  className,
  id,
  dataTestId,
  enableAzureSearch = false,
  provisionRole = "mangelbearbeiter",
  assignmentContext,
}: ProfileSelectProps) {
  const { t } = useTranslation();
  const { profile } = useAuth();
  // Hoist the specific property to a primitive so manual-memo deps match what the
  // compiler infers (react-hooks/preserve-manual-memoization, TES-1064). Behavior
  // unchanged — same value, same memoization triggers.
  const activeTenantId = profile?.active_tenant_id;
  const [searchQuery, setSearchQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const { azureUsers, isSearching, isProvisioning, searchUsers, provisionUser, clearResults } =
    useAzureUserSearch(profile?.active_tenant_id || undefined, assignmentContext);

  const defaultPlaceholder = placeholder || t("common.selectUser", "Benutzer auswählen...");
  const defaultUnassignedLabel = unassignedLabel || t("common.notAssigned", "Nicht zugewiesen");

  // Find selected profile for trigger display
  const selectedProfile = profiles.find((p) => p.user_id === value);

  // Debounced Azure search
  useEffect(() => {
    if (!enableAzureSearch || !isOpen) return;

    const timeoutId = setTimeout(() => {
      if (searchQuery.length >= 3) {
        searchUsers(searchQuery);
      } else {
        clearResults();
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, enableAzureSearch, isOpen, searchUsers, clearResults]);

  // Filter local profiles by search query
  const filteredProfiles = useMemo(() => {
    if (!searchQuery) return profiles;
    const lowerQuery = searchQuery.toLowerCase();
    return profiles.filter((p) => {
      const displayName = getProfileDisplayName(p).toLowerCase();
      const email = p.email?.toLowerCase() || "";
      return displayName.includes(lowerQuery) || email.includes(lowerQuery);
    });
  }, [profiles, searchQuery]);

  // Declared before the callback that references it (react-hooks/immutability, TES-1064).
  const handleProvisionAndSelect = useCallback(
    async (azureUser: AzureUser) => {
      if (!activeTenantId) return;

      try {
        const result = await provisionUser(azureUser, activeTenantId, provisionRole);
        onValueChange(result.user_id);
        toast.success(t("common.userProvisioned", "Benutzer erfolgreich angelegt"));
      } catch (error) {
        console.error("Failed to provision user:", error);
        toast.error(t("common.error", "Fehler"));
      }
    },
    [activeTenantId, provisionUser, onValueChange, provisionRole, t],
  );

  // Handle the special "unassigned" value
  const handleValueChange = useCallback(
    (newValue: string) => {
      if (newValue === "__unassigned__") {
        onValueChange("");
      } else if (newValue.startsWith("azure:")) {
        // Azure user selected - provision and then callback with user_id
        const azureOid = newValue.slice(6);
        const azureUser = azureUsers.find((u) => u.azure_oid === azureOid);
        if (azureUser && activeTenantId) {
          handleProvisionAndSelect(azureUser);
        }
      } else {
        onValueChange(newValue);
      }
    },
    [onValueChange, azureUsers, activeTenantId, handleProvisionAndSelect],
  );

  // Convert empty value to special unassigned value for Select
  const selectValue = value || (allowUnassigned ? "__unassigned__" : "");

  // Clear search when closing
  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      if (!open) {
        setSearchQuery("");
        clearResults();
      }
    },
    [clearResults],
  );

  const hasAzureResults = enableAzureSearch && azureUsers.length > 0;
  const showSearchHint = enableAzureSearch && searchQuery.length > 0 && searchQuery.length < 3;

  return (
    <Select
      value={selectValue}
      onValueChange={handleValueChange}
      disabled={disabled || loading || isProvisioning}
      open={isOpen}
      onOpenChange={handleOpenChange}
    >
      {/*
        TES-879: aria-label gives the people-picker combobox a discernible name (WCAG select-name)
        for callers that render it WITHOUT a wrapping <Label>. TES-2273 (Copilot review): when `id`
        IS set, every current call site also renders a matching external `<Label htmlFor={id}>` —
        an explicit aria-label there SHADOWS that label for accessible-name computation, so it's
        announced as the placeholder ("Benutzer auswählen...") instead of the field's real purpose
        ("Zugewiesen an *"), and getByLabel(/zugewiesen/i) can never match it either. Only fall back
        to aria-label when there's no id-linked external label to do the job.
      */}
      <SelectTrigger
        id={id}
        data-testid={dataTestId}
        className={cn(className)}
        aria-label={id ? undefined : defaultPlaceholder}
      >
        <SelectValue placeholder={defaultPlaceholder}>
          {loading || isProvisioning ? (
            <span className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {isProvisioning
                ? t("common.provisioningUser", "Benutzer wird angelegt...")
                : t("common.loading", "Laden...")}
            </span>
          ) : selectedProfile ? (
            <span className="flex items-center gap-2">
              <ProfileAvatar profile={selectedProfile} size="xs" />
              <span className="truncate">{getProfileDisplayName(selectedProfile)}</span>
              {selectedProfile.is_provisioned && (
                <Cloud className="h-3 w-3 text-muted-foreground" />
              )}
            </span>
          ) : allowUnassigned && !value ? (
            <span className="text-muted-foreground">{defaultUnassignedLabel}</span>
          ) : (
            <span className="text-muted-foreground">{defaultPlaceholder}</span>
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {/* Search input for Azure-enabled mode */}
        {enableAzureSearch && (
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t("common.search", "Suchen...")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-8"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            {showSearchHint && (
              <p className="text-xs text-muted-foreground mt-1">
                {t("common.searchMinChars", "Mind. 3 Zeichen eingeben...")}
              </p>
            )}
          </div>
        )}

        {loading ? (
          <SelectItem value="__loading__" disabled>
            <span className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t("common.loading", "Laden...")}
            </span>
          </SelectItem>
        ) : (
          <>
            {/* Unassigned option */}
            {allowUnassigned && (
              <SelectItem value="__unassigned__">
                <span className="text-muted-foreground">{defaultUnassignedLabel}</span>
              </SelectItem>
            )}

            {/* Local users section */}
            {filteredProfiles.length > 0 && (
              <SelectGroup>
                {hasAzureResults && (
                  <SelectLabel className="flex items-center gap-2 text-xs">
                    <Users className="h-3 w-3" />
                    {t("common.localUsers", "Lokale Benutzer")}
                  </SelectLabel>
                )}
                {filteredProfiles.map((profile) => (
                  <SelectItem
                    key={profile.user_id}
                    value={profile.user_id}
                    data-testid={`profile-option-${profile.user_id}`}
                  >
                    <span className="flex items-center gap-2">
                      <ProfileAvatar profile={profile} size="xs" />
                      <span className="truncate">{getProfileDisplayName(profile)}</span>
                      {profile.is_provisioned && (
                        <Cloud className="h-3 w-3 text-muted-foreground" />
                      )}
                    </span>
                  </SelectItem>
                ))}
              </SelectGroup>
            )}

            {/* Azure AD users section */}
            {enableAzureSearch && (
              <>
                {isSearching && (
                  <div className="flex items-center gap-2 p-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t("common.searchingAzure", "Suche in Azure AD...")}
                  </div>
                )}

                {hasAzureResults && (
                  <SelectGroup>
                    <SelectLabel className="flex items-center gap-2 text-xs">
                      <Cloud className="h-3 w-3" />
                      {t("common.azureAdUsers", "Azure AD")}
                    </SelectLabel>
                    {azureUsers.map((user) => (
                      <SelectItem key={user.azure_oid} value={`azure:${user.azure_oid}`}>
                        <span className="flex items-center gap-2">
                          <ProfileAvatar
                            profile={{
                              first_name: user.first_name,
                              last_name: user.last_name,
                              display_name: user.display_name,
                              email: user.email,
                              avatar_url: null,
                            }}
                            size="xs"
                          />
                          <span className="truncate">{user.display_name || user.email}</span>
                          {user.azure_app_role && (
                            <Badge
                              variant="secondary"
                              className="shrink-0 text-[10px] px-1.5 py-0"
                              title={t("userManagement.azureAppRoleTooltip")}
                            >
                              {user.azure_app_role}
                            </Badge>
                          )}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectGroup>
                )}

                {!isSearching && searchQuery.length >= 3 && azureUsers.length === 0 && (
                  <div className="p-2 text-sm text-muted-foreground text-center">
                    {t("common.noAzureResults", "Keine Benutzer in Azure AD gefunden")}
                  </div>
                )}
              </>
            )}

            {/* No results at all */}
            {!loading && filteredProfiles.length === 0 && !hasAzureResults && !isSearching && (
              <div className="p-2 text-sm text-muted-foreground text-center">
                {t("common.noData", "Keine Daten verfügbar")}
              </div>
            )}
          </>
        )}
      </SelectContent>
    </Select>
  );
}
